import { useState, useEffect } from "react";
import { Shield, QrCode, CheckCircle, AlertCircle, Copy, Check } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../components/ui/dialog";
import axios from "axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

export default function Setup2FA({ usuario, isOpen, onClose, onSuccess }) {
  const [step, setStep] = useState(1); // 1: QR Code, 2: Verificar código
  const [loading, setLoading] = useState(false);
  const [qrCode, setQrCode] = useState("");
  const [secret, setSecret] = useState("");
  const [codigo, setCodigo] = useState("");
  const [erro, setErro] = useState("");
  const [copiado, setCopiado] = useState(false);

  useEffect(() => {
    if (isOpen && usuario?.id) {
      gerarQRCode();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, usuario?.id]);

  const gerarQRCode = async () => {
    setLoading(true);
    setErro("");
    try {
      const response = await axios.post(
        `${API}/api/auth/2fa/setup`,
        null,
        { params: { user_id: usuario.id } }
      );

      if (response.data.success) {
        setQrCode(response.data.qr_code);
        setSecret(response.data.secret);
        setStep(1);
      } else {
        setErro(response.data.message || "Erro ao gerar QR Code");
      }
    } catch (error) {
      console.error("Erro ao configurar 2FA:", error);
      setErro("Erro ao gerar QR Code. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const verificarCodigo = async () => {
    if (codigo.length !== 6) {
      setErro("Digite o código de 6 dígitos");
      return;
    }

    setLoading(true);
    setErro("");
    try {
      const response = await axios.post(
        `${API}/api/auth/2fa/verify-setup`,
        null,
        { params: { user_id: usuario.id, code: codigo } }
      );

      if (response.data.success) {
        toast.success("2FA ativado com sucesso!");
        onSuccess?.();
        onClose();
      } else {
        setErro(response.data.message || "Código inválido");
      }
    } catch (error) {
      console.error("Erro ao verificar código:", error);
      setErro("Erro ao verificar código. Tente novamente.");
    } finally {
      setLoading(false);
    }
  };

  const copiarSecret = () => {
    navigator.clipboard.writeText(secret);
    setCopiado(true);
    toast.success("Código copiado!");
    setTimeout(() => setCopiado(false), 2000);
  };

  const handleCodigoChange = (e) => {
    const valor = e.target.value.replace(/\D/g, "").slice(0, 6);
    setCodigo(valor);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-green-600" />
            Configurar Autenticação de 2 Fatores
          </DialogTitle>
          <DialogDescription>
            Proteja sua conta com o Google Authenticator
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Step 1: QR Code */}
          {step === 1 && (
            <>
              <div className="text-center space-y-4">
                <p className="text-sm text-gray-600">
                  1. Baixe o app <strong>Google Authenticator</strong> no seu celular
                </p>
                <p className="text-sm text-gray-600">
                  2. Escaneie o QR Code abaixo com o app
                </p>

                {loading ? (
                  <div className="h-48 flex items-center justify-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                  </div>
                ) : qrCode ? (
                  <div className="flex justify-center">
                    <div className="p-4 bg-white rounded-lg shadow-inner border">
                      <img src={qrCode} alt="QR Code 2FA" className="w-48 h-48" />
                    </div>
                  </div>
                ) : null}

                {/* Código manual */}
                {secret && (
                  <div className="mt-4 p-3 bg-gray-100 rounded-lg">
                    <p className="text-xs text-gray-500 mb-1">
                      Ou digite o código manualmente:
                    </p>
                    <div className="flex items-center justify-center gap-2">
                      <code className="text-sm font-mono bg-white px-2 py-1 rounded border">
                        {secret}
                      </code>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={copiarSecret}
                        className="h-8 w-8 p-0"
                      >
                        {copiado ? (
                          <Check className="w-4 h-4 text-green-600" />
                        ) : (
                          <Copy className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  </div>
                )}
              </div>

              <Button
                className="w-full"
                onClick={() => setStep(2)}
                disabled={!qrCode}
              >
                Continuar
              </Button>
            </>
          )}

          {/* Step 2: Verificar código */}
          {step === 2 && (
            <>
              <div className="text-center space-y-4">
                <p className="text-sm text-gray-600">
                  Digite o código de 6 dígitos mostrado no Google Authenticator
                </p>

                <div className="flex justify-center">
                  <Input
                    type="text"
                    placeholder="000000"
                    value={codigo}
                    onChange={handleCodigoChange}
                    className="text-center text-2xl font-mono tracking-widest max-w-[160px]"
                    maxLength={6}
                    autoFocus
                    data-testid="2fa-code-input"
                  />
                </div>

                {erro && (
                  <div className="flex items-center justify-center gap-2 text-red-600 text-sm">
                    <AlertCircle className="w-4 h-4" />
                    <span>{erro}</span>
                  </div>
                )}
              </div>

              <div className="flex gap-2">
                <Button
                  variant="outline"
                  className="flex-1"
                  onClick={() => setStep(1)}
                >
                  Voltar
                </Button>
                <Button
                  className="flex-1"
                  onClick={verificarCodigo}
                  disabled={codigo.length !== 6 || loading}
                  data-testid="verify-2fa-button"
                >
                  {loading ? "Verificando..." : "Ativar 2FA"}
                </Button>
              </div>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
